import React, { useRef, useState } from 'react'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import AutocompleteBase from '../Form/Fields/AutocompleteBase/AutocompleteBase'
import { pageStatuses } from './Enums'
import { IAddress, IContent } from './types'
import { IS_PROD, IS_STORYBOOK } from '../../utils/constants'
import { FieldType } from '../Form/utils/fieldTypeEnum'
import OutageInfo from './OutageInfo'
import { mockOutages, mockContent } from './Telephony/utils/driftsinfoData.mock'

interface AddressFormInput {
  dawa: string
}

const getSearchString = (query: string) =>
  `https://dawa.aws.dk/autocomplete?q=${query}*&type=adresse&caretpos=10&fuzzy=false&per_side:100`
const getDawaIdUrl = (id: string) => `https://dawa.aws.dk/adresser/${id}?struktur=mini`
const madIdUrl =
  IS_PROD && !IS_STORYBOOK
    ? `https://prod-a.digital-help.prod.managed-eks.aws.nuuday.nu/ghapi/kundeservice/madsrv-service-dn/search`
    : `https://test-a.digital-help.non-prod.managed-eks.aws.nuuday.nu/ghapi/kundeservice/madsrv-service-dn/search`
const outageInfoUrl = (madId: string, amsId: string) =>
  IS_PROD && !IS_STORYBOOK
    ? `https://prod-a.digital-help.prod.managed-eks.aws.nuuday.nu/ghapi/kundeservice/driftinformation/formatedResponse?madId=${madId}&amsId=${amsId}`
    : `https://test-a.digital-help.non-prod.managed-eks.aws.nuuday.nu/ghapi/kundeservice/driftinformation/formatedResponse?madId=${madId}&amsId=${amsId}`

const { INITIAL, LOADING, RESULTS } = pageStatuses

const AutoComplete = ({ content }: { content: IContent }) => {
  const [open, setOpen] = useState(false)
  const [status, setStatus] = useState(INITIAL)
  const [outages, setOutages] = useState([])
  const inputRef = useRef<HTMLInputElement>(null)

  const setCaret = (value: number) => {
    if (inputRef.current) {
      setTimeout(() => {
        inputRef.current && inputRef.current.focus()
        inputRef.current && inputRef.current.setSelectionRange(value, value)
      }, 1)
    }
  }

  const form = useForm<AddressFormInput>()

  const handleSelect = async (selectedAddress: any) => {
    if (!selectedAddress) {
      setStatus(INITIAL)
      setOutages([])
      setOpen(false)
      return
    }
    setOpen(true)
    if (selectedAddress.data.adgangsadresseid) {
      setOpen(false)
      setStatus(LOADING)
      const addressRes = await fetch(getDawaIdUrl(selectedAddress.data.id))
      const address = await addressRes.json()
      const addressIdRes = await fetch(madIdUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify({
          transactionId: '123123123',
          postalCode: address.postnr,
          streetCode: address.vejkode,
          streetName: address.vejnavn,
          municipalityCode: address.kommunekode,
          buildingIdentifier: address.husnr,
          floor: address.etage,
          suite: address['dør'],
        }),
      })
      const addressIdData = await addressIdRes.json()

      const outageRes = await fetch(outageInfoUrl(addressIdData.specificMadId, addressIdData.amsId))
      const outageData = await outageRes.json()
      setStatus(RESULTS)
      const broadbandOutageData = outageData.issues
        ? outageData.issues.filter((issue: any) => issue.effectedProducts.includes('BROADBAND'))
        : []
      setOutages(broadbandOutageData || [])
    } else {
      setCaret(selectedAddress.caretpos)
    }
  }

  return (
    <>
      <FormProvider {...form}>
        <form>
          <Controller
            name="dawa"
            control={form.control}
            render={field => (
              <AutocompleteBase
                {...field}
                autoSelect={false}
                data-testid="linevisitation-autocomplete"
                fieldType={FieldType.ADDRESS}
                placeholder="Indtast adresse"
                autoComplete={true}
                //@ts-ignore
                getOptionLabel={(address: IAddress) => address.forslagstekst}
                //@ts-ignore
                getOptionSelected={(value: IAddress, option: IAddress) => value.madId === option.madId}
                inputRef={inputRef}
                modelData={data => data}
                name="dawa"
                onChange={handleSelect}
                open={open}
                required={true}
                urlBuilder={getSearchString}
              />
            )}
          />
        </form>
      </FormProvider>
      {status !== 'INITIAL' && (
        <OutageInfo
          status={status}
          outages={IS_STORYBOOK ? mockOutages : outages}
          content={IS_STORYBOOK ? mockContent : content}
        />
      )}
    </>
  )
}

export default AutoComplete
